import { useEffect, useMemo } from 'react';

import type { SelectFilterOptionChange } from '../../../common/constants/filters/select-filter/types';
import { serveAutoAppliedFiltersExperiment } from '../../../common/utils/experiments/experiment';
import {
	convertDateRangeToLastUpdated,
	getContributorsFromParticipants,
} from '../../../common/utils/filters';
import { useSearchActions } from '../../../controllers/store/search';

import type { searchResultsBody_AtlassianSearchPage$data } from './__generated__/searchResultsBody_AtlassianSearchPage.graphql';

type AutoAppliedFiltersInput = NonNullable<
	NonNullable<searchResultsBody_AtlassianSearchPage$data['autoAppliedFilters']>
>['commonFilters'];

type AutoFilterConfig = {
	filterId: string;
	optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
	options?: {
		customValue?: Record<string, string>;
	};
};

const transformAutoAppliedFilters = (
	autoAppliedFilters: AutoAppliedFiltersInput,
): AutoFilterConfig[] => {
	const filters: AutoFilterConfig[] = [];

	const { range, participants } = autoAppliedFilters ?? {};

	// Handle lastModified range filter
	const lastUpdated = convertDateRangeToLastUpdated(range?.lastModified);
	if (lastUpdated !== 'anytime') {
		filters.push({
			filterId: 'lastUpdated',
			optionChange: [{ value: lastUpdated, isSelected: true }],
			options:
				lastUpdated === 'custom'
					? {
							customValue: {
								from: range?.lastModified?.gt ?? '',
								to: range?.lastModified?.lt ?? '',
							},
						}
					: undefined,
		});
	}

	// Handle contributor filter from participants
	if (participants?.items && participants.combination) {
		const contributorUserIds = getContributorsFromParticipants({
			combination: participants.combination,
			items: participants.items,
		});

		if (contributorUserIds.length > 0) {
			filters.push({
				filterId: 'contributor',
				optionChange: contributorUserIds.map((userId) => ({ value: userId, isSelected: true })),
			});
		}
	}

	return filters;
};

export type AutoAppliedFilterInfo = {
	filterId: string;
	value: string;
};

const toAnalyticsFormat = (filters: AutoFilterConfig[]): AutoAppliedFilterInfo[] => {
	return filters.flatMap((filter) => {
		const optionChanges = Array.isArray(filter.optionChange)
			? filter.optionChange
			: [filter.optionChange];
		return optionChanges.map((option) => ({
			filterId: filter.filterId,
			value: option.value,
		}));
	});
};

type useSearchResultsAutoFiltersArgs = {
	autoAppliedFilters: AutoAppliedFiltersInput;
	isPaginatedQuery: boolean;
};

export const useSearchResultsAutoFilters: ({
	autoAppliedFilters,
	isPaginatedQuery,
}: useSearchResultsAutoFiltersArgs) => {
	autoAppliedFilters: AutoAppliedFilterInfo[];
} = ({ autoAppliedFilters, isPaginatedQuery }: useSearchResultsAutoFiltersArgs) => {
	const { setAutoFilters } = useSearchActions();

	const transformedFilters = useMemo(() => {
		if (!serveAutoAppliedFiltersExperiment({ fireExperimentExposure: false })) {
			return [];
		}

		if (isPaginatedQuery) {
			return [];
		}

		return transformAutoAppliedFilters(autoAppliedFilters);
	}, [isPaginatedQuery, autoAppliedFilters]);

	const autoAppliedFiltersForAnalytics = useMemo(
		() => toAnalyticsFormat(transformedFilters),
		[transformedFilters],
	);

	useEffect(() => {
		if (!serveAutoAppliedFiltersExperiment({ fireExperimentExposure: true })) {
			return;
		}

		if (transformedFilters.length > 0) {
			setAutoFilters(transformedFilters);
		}
	}, [setAutoFilters, transformedFilters]);

	return { autoAppliedFilters: autoAppliedFiltersForAnalytics };
};
